import angular from 'angular';
import template from 'url-template';
import pick from 'lodash.pick';

const configOptions = ['cache'];

function resource($http, $parse, $log) {

  function whitelist(arg) {
    return pick(arg, configOptions);
  }

  class Resource {

    static showDeprecationMessage() {
      $log.warn('Resource: using fetch to return an array of objects is deprecated. Please use "fetchAll". This will be removed in akamai-core 1.8.0.'); // eslint-disable-line max-len
    }

    // set Content-Type header for PUT and POST
    static get type() {
      return 'application/json';
    }

    // TODO: Verify this works in 1.5.x
    // looks wrong, but this is supported by $parse. It's just not documented.
    // https://github.com/angular/angular.js/blob/v1.4.7/src/ng/parse.js#L628
    static root() {
      return 'this';
    }

    constructor(data = {}) {
      angular.merge(this, data);
    }

    /**
     * Used to
     * @private
     * @param {Object} args arguments to resolve the URL
     * @returns {String} URL to fetch
     */
    static url(args = {}) {
      if (angular.isUndefined(this.template)) {
        throw Error('template URI required for Resource');
      }

      const uri = angular.isFunction(this.template) ?
        Reflect.apply(this.template, this, [args]) : this.template;

      const url = template.parse(uri);

      return url.expand(args);
    }

    /**
     * Fetches a model (or an array of models) from the server
     * @param {Object} args arguments to resolve the URL
     * @param {Object} config The configuration object used to generate the request.
     * @returns {Promise} A promise to fetch a model
     */
    static fetch(args, config = {}) {
      const myConfig = whitelist(config);

      return $http.get(this.url(args), myConfig)
        .then(result => {
          const data = $parse(this.root())(result.data) || result.data;

          if (angular.isArray(data)) {
            Resource.showDeprecationMessage();
            return data.map(item => Reflect.construct(this, [item]));
          }
          return Reflect.construct(this, [data]);
        });
    }

    static fetchAll(args, config = {}) {
      const myConfig = whitelist(config);
      return $http.get(this.url(args), myConfig)
        .then(results => $parse(this.root())(results.data) || results.data)
        .then(results => {
          if (!angular.isArray(results)) {
            $log('Resource: fetchAll called and didn not receive array response');
          }
          return results.map(item => Reflect.construct(this, [item]));
        });
    }

    /**
     * Saves a representation of this model to the server
     * @param {Object} args arguments to resolve the URL
     * @param {Object} data data to POST to the server
     * @returns {Promise} A promise to create a new model
     */
    static save(args, data) {
      return $http.post(this.url(args), data, {
        headers: {
          'Content-Type': this.type
        }
      })
        .then(result => Reflect.construct(this, [result.data]));
    }

    /**
     * Fetches data for an instance of a resource.
     * Will update the current model with server result.
     * @param {Object} args arguments to resolve the URL
     * @param {Object} config The configuration object used to generate the request.
     * @returns {Promise} promise to update the model
     */
    fetch(args, config = {}) {
      const myConfig = whitelist(config);
      return Reflect.apply(Resource.fetch, this.constructor, [angular.merge({}, this, args),
        myConfig])
        .then(data => angular.copy(data, this));
    }

    /**
     * Saves an individual model. Does a PUT request (for now)
     * @param {Object} args arguments to resolve the URL
     * @returns {Promise} A promise to update the model
     */
    save(args) {
      return $http.put(this.constructor.url(angular.merge({}, this, args)), this, {
        headers: {
          'Content-Type': this.constructor.type
        }
      })
        .then(result => Reflect.construct(this.constructor, [result.data]));
    }

    /**
     * Deletes an individual model via a DELETE request
     * @param {Object} args arguments to resolve the URL
     * @returns {Promise} A promise to delete the model
     */
    delete(args) {
      return $http.delete(this.constructor.url(angular.merge({}, this, args)));
    }
  }

  return Resource;

}

resource.$inject = ['$http', '$parse', '$log'];

export default resource;
